import "./LLMMainPanel.scss";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { LLMMessage, SessionId } from "@blisspointmedia/bpm-types/dist/LLM";
import { Spinner } from "../../Components/Spinner";
import { FloatingPanelActionButton } from "../../Components/FloatingPanel/FloatingPanel";
import { ACKNOWLEDGEMENT_PHRASES } from "../LLMConstants";
import { LLMMessageBox } from "../LLMMessageBox/LLMMessageBox";
import { ReactComponent as NewChatIcon } from "../NewChat.svg";
import { NewChat } from "../NewChat/NewChat";
import { PromptInput, PromptInputProps } from "../PromptInput/PromptInput";

interface LLMMainPanelLeftActionsProps {
  startNewChat: () => void;
}

const LLMMainPanelLeftActions = ({ startNewChat }: LLMMainPanelLeftActionsProps): JSX.Element => {
  return (
    <FloatingPanelActionButton
      icon={<NewChatIcon />}
      onClick={startNewChat}
      tooltipText="Start new chat"
    />
  );
};

interface LLMMainPanelBodyProps {
  isOpen: boolean;
  messages: LLMMessage[] | undefined;
  sessionId?: SessionId;
  updateMessage: (
    sessionId: string | undefined,
    updatedMsg: Pick<LLMMessage, "messageId"> & Partial<Omit<LLMMessage, "messageId">>
  ) => void;
  submitting: boolean;
  setDraftMessage: Dispatch<SetStateAction<string>>;
}

const LLMMainPanelBody = ({
  isOpen,
  messages,
  sessionId,
  updateMessage,
  submitting,
  setDraftMessage,
}: LLMMainPanelBodyProps): JSX.Element => {
  // ref given to the div of the bottom-most message in the chat
  const messagesScrollRef = useRef<HTMLDivElement | null>(null);
  const [messageLengthAtInitialScroll, setMessageLengthAtInitialScroll] = useState<
    number | undefined
  >();
  const [acknowledgementPhrase, setAcknowledgementPhrase] = useState<string | undefined>();
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | undefined>();

  useEffect(() => {
    // Try to find the scroll container
    const findScrollContainer = () => {
      const container = document.querySelector("#llm-floating-panel .mainPanel .floatingPanelBody");
      if (container) {
        setScrollContainer(container as HTMLDivElement);
        return true;
      }
      return false;
    };

    // Try immediately
    if (findScrollContainer()) {
      return;
    }

    // If not found, poll every 100ms
    const interval = setInterval(() => {
      if (findScrollContainer()) {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  // Reset initial scroll on session change
  useEffect(() => {
    if (messages === undefined) {
      setMessageLengthAtInitialScroll(undefined);
    }
  }, [messages]);

  // Instant scroll to bottom of messages on initial load
  useEffect(() => {
    if (isOpen && messages && messageLengthAtInitialScroll === undefined && scrollContainer) {
      scrollContainer.scrollTo({ top: scrollContainer.scrollHeight });
      setMessageLengthAtInitialScroll(messages.length);
    }
  }, [isOpen, messageLengthAtInitialScroll, messages, scrollContainer]);

  // Smooth scroll to the top new newly received messages
  useEffect(() => {
    if (
      isOpen &&
      messageLengthAtInitialScroll &&
      messages?.length !== messageLengthAtInitialScroll &&
      messagesScrollRef.current &&
      scrollContainer
    ) {
      const SCROLL_PADDING = 16;
      const parentPadding =
        scrollContainer.scrollHeight - (messagesScrollRef.current.parentElement?.scrollHeight ?? 0);
      const targetPosition =
        scrollContainer.scrollHeight -
        parentPadding -
        messagesScrollRef.current.offsetHeight -
        SCROLL_PADDING;

      scrollContainer.scrollTo({
        top: targetPosition,
        behavior: "smooth",
      });
    }
  }, [isOpen, messageLengthAtInitialScroll, messages, scrollContainer]);

  useEffect(() => {
    if (!submitting) {
      setAcknowledgementPhrase(undefined);
    }
  }, [submitting]);

  // Set random acknowledgement phrase and rotate every 15 seconds while submitting
  useEffect(() => {
    if (!submitting) {
      return;
    }

    const getRandomPhrase = () => {
      const randomIndex = Math.floor(Math.random() * ACKNOWLEDGEMENT_PHRASES.length);
      return ACKNOWLEDGEMENT_PHRASES[randomIndex];
    };

    const setFirstPhrase = async () => {
      await setAcknowledgementPhrase(getRandomPhrase());

      // Smooth scroll to the bottom after the acknowledgement phrase has rendered
      if (scrollContainer) {
        scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: "smooth" });
      }
    };

    setFirstPhrase();

    const FIFTEEN_SECONDS = 15000;
    const interval = setInterval(() => {
      setAcknowledgementPhrase(getRandomPhrase());
    }, FIFTEEN_SECONDS);

    return () => clearInterval(interval);
  }, [scrollContainer, submitting]);

  if (!messages) {
    return (
      <div className="loadingMessages">
        <Spinner />
      </div>
    );
  }

  if (messages.length === 0) {
    return <NewChat onSelect={setDraftMessage} />;
  }

  return (
    <div className="messagesContainer">
      {messages.map((message, index) => {
        const isLastMessage = index === messages.length - 1;
        const prevTimestamp = index === 0 ? null : messages[index - 1].timestamp;

        return (
          <LLMMessageBox
            key={`message-${index}`}
            message={message}
            isLastMessage={isLastMessage}
            scrollRef={messagesScrollRef}
            prevTimestamp={prevTimestamp}
            setMessageFeedback={newMsg =>
              updateMessage(sessionId, { ...newMsg, messageId: message.messageId })
            }
          />
        );
      })}
      {acknowledgementPhrase && (
        <div className="submittingAcknowledgement">{acknowledgementPhrase}</div>
      )}
    </div>
  );
};

const LLMMainPanel = {
  LeftActions: LLMMainPanelLeftActions,
  Body: LLMMainPanelBody,
  Footer: (props: PromptInputProps): JSX.Element => <PromptInput {...props} />,
};

export default LLMMainPanel;
